import React, { useEffect, useState, useCallback } from "react";
import { Bell, BellOff, Smartphone, Check, AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  pushSupported,
  getFullStatus,
  subscribePush,
  unsubscribePush,
  testPush,
} from "@/lib/push";

function isStandalone() {
  return (
    window.matchMedia && window.matchMedia("(display-mode: standalone)").matches
  ) || window.navigator.standalone === true;
}

function isIOS() {
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

/**
 * PushToggle — small card to enable/disable push notifications on this device.
 * Truth-source: backend. Shows "Notifiche attive" ONLY when the backend has
 * THIS browser's endpoint registered for the current user.
 */
export default function PushToggle({ compact = false }) {
  const [supported, setSupported] = useState(true);
  const [status, setStatus] = useState({
    permission: typeof Notification !== "undefined" ? Notification.permission : "default",
    active: false,
    needsResync: false,
    backendCount: 0,
    serverEnabled: true,
    error: null,
  });
  const [loading, setLoading] = useState(false);
  const iOSNeedsInstall = isIOS() && !isStandalone();

  const refresh = useCallback(async () => {
    if (!pushSupported()) {
      setSupported(false);
      return;
    }
    try {
      const s = await getFullStatus();
      setStatus(s);
    } catch (e) {
      console.warn("[push] refresh failed", e);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-refresh when the tab becomes visible again (user may have changed perms in another tab)
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  const enable = async () => {
    setLoading(true);
    try {
      await subscribePush();
      await refresh();
      toast.success("Notifiche attive su questo dispositivo");
    } catch (e) {
      console.error("[push] enable failed", e);
      toast.error(e.message || "Impossibile attivare le notifiche");
    } finally {
      setLoading(false);
    }
  };

  const resync = async () => {
    setLoading(true);
    try {
      await subscribePush({ resyncOnly: true });
      await refresh();
      toast.success("Dispositivo registrato sul server");
    } catch (e) {
      console.error("[push] resync failed", e);
      toast.error(e.message || "Impossibile registrare questo dispositivo");
    } finally {
      setLoading(false);
    }
  };

  const disable = async () => {
    setLoading(true);
    try {
      await unsubscribePush();
      await refresh();
      toast("Notifiche disattivate su questo dispositivo");
    } finally {
      setLoading(false);
    }
  };

  const sendTest = async () => {
    setLoading(true);
    try {
      const r = await testPush();
      if (r.sent > 0) {
        toast.success(`Notifica di test inviata a ${r.sent} dispositiv${r.sent > 1 ? 'i' : 'o'}!`);
      } else if (r.registered === 0) {
        toast.error("Nessun dispositivo registrato sul server. Tocca 'Registra questo dispositivo'.");
        await refresh();
      } else {
        const code = r.last_failure_status;
        const codeMsg = code ? ` (codice ${code})` : "";
        toast.error(`Invio fallito${codeMsg}. ${r.removed > 0 ? "Riconfigura il dispositivo." : "Riprova o controlla i log."}`);
      }
    } catch (e) {
      console.error("[push] test failed", e);
      toast.error("Errore invio test");
    } finally {
      setLoading(false);
    }
  };

  if (!supported) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-200">
        <div className="flex items-center gap-2 font-medium">
          <AlertCircle className="w-4 h-4" /> Notifiche non supportate
        </div>
        <p className="mt-1 text-amber-800 dark:text-amber-300">
          Il tuo browser non supporta le notifiche push. Usa Chrome, Edge, Firefox (Android/desktop) o Safari iOS 16.4+.
        </p>
      </div>
    );
  }

  if (iOSNeedsInstall) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900" data-testid="push-ios-install-hint">
        <div className="flex items-center gap-2 font-medium">
          <Smartphone className="w-4 h-4" /> Aggiungi alla schermata Home
        </div>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
          Su iPhone/iPad le notifiche push funzionano solo dopo aver installato l'app:
          tocca <strong>Condividi</strong> &rarr; <strong>Aggiungi a Home</strong>, poi apri
          Prenotoo dall'icona.
        </p>
      </div>
    );
  }

  if (status.serverEnabled === false) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-200" data-testid="push-server-disabled">
        <div className="flex items-center gap-2 font-medium">
          <AlertCircle className="w-4 h-4" /> Notifiche non disponibili
        </div>
        <p className="mt-1 text-amber-800 dark:text-amber-300">Il server non è ancora configurato per le notifiche push. Riprova più tardi.</p>
      </div>
    );
  }

  const { active, needsResync, permission, backendCount } = status;

  return (
    <div
      className={
        compact
          ? "flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-white p-3"
          : "rounded-2xl border border-zinc-200 bg-white p-4"
      }
      data-testid="push-toggle-card"
      data-active={active ? "1" : "0"}
      data-needs-resync={needsResync ? "1" : "0"}
    >
      <div className={compact ? "flex items-center gap-3" : "mb-3"}>
        <div className={compact ? "" : "flex items-center gap-2 font-medium"}>
          {active ? <Bell className="w-4 h-4 text-emerald-600" /> : <BellOff className="w-4 h-4 text-zinc-400 dark:text-zinc-500" />}
          <span className={compact ? "text-sm font-medium" : ""}>
            {active ? "Notifiche attive su questo dispositivo" : needsResync ? "Dispositivo non registrato" : "Notifiche push"}
          </span>
        </div>
        {!compact && (
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            {active && (backendCount > 1
              ? `Riceverai notifiche su ${backendCount} dispositivi associati a questo account.`
              : "Riceverai notifiche su questo dispositivo per conferme, promemoria e aggiornamenti.")}
            {!active && needsResync && "Hai dato il permesso ma il server non riconosce questo dispositivo. Tocca \"Registra questo dispositivo\" per sincronizzare."}
            {!active && !needsResync && "Attiva per ricevere promemoria e aggiornamenti in tempo reale, anche ad app chiusa."}
          </p>
        )}
        {permission === "denied" && (
          <p className="mt-2 text-xs text-rose-700 dark:text-rose-300">
            Hai negato il permesso. Sbloccalo dalle impostazioni del browser per continuare.
          </p>
        )}
      </div>
      <div className={compact ? "flex gap-2 flex-wrap" : "flex flex-wrap gap-2"}>
        {needsResync && (
          <Button size="sm" onClick={resync} disabled={loading} data-testid="push-resync-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Registra questo dispositivo
          </Button>
        )}
        {!active && !needsResync && (
          <Button
            size="sm"
            onClick={enable}
            disabled={loading || permission === "denied"}
            data-testid="push-enable-btn"
          >
            <Bell className="w-4 h-4 mr-1" /> Attiva
          </Button>
        )}
        {active && (
          <>
            <Button size="sm" variant="outline" onClick={sendTest} disabled={loading} data-testid="push-test-btn">
              <Check className="w-4 h-4 mr-1" /> Invia test
            </Button>
            <Button size="sm" variant="ghost" onClick={disable} disabled={loading} data-testid="push-disable-btn">
              <BellOff className="w-4 h-4 mr-1" /> Disattiva
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
